<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'next/icon/Icon.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const report = useMapGetter('campaigns/getCampaignReport');
const searchTerm = ref('');
const statusFilter = ref('');

const isLoading = computed(() => uiFlags.value.isFetchingReport);
const campaignId = computed(() => route.params.campaignId);
const isDownloading = ref(false);

const downloadReport = async () => {
  try {
    isDownloading.value = true;
    await store.dispatch('campaigns/downloadReport', {
      campaignId: campaignId.value,
      params: {
        status: statusFilter.value,
        search: searchTerm.value,
      },
    });
  } catch (error) {
    useAlert(t('SLA_REPORTS.DOWNLOAD_FAILED'));
  } finally {
    isDownloading.value = false;
  }
};

// Filters & pagination state
const currentPage = ref(1);
const perPage = ref(10);

const statusOptions = [
  { value: '', label: () => t('CAMPAIGN.WHATSAPP.REPORT.ALL_STATUSES') },
  {
    value: 'sent',
    label: () => t('CAMPAIGN.WHATSAPP.REPORT.STATUS_BADGE.SENT'),
  },
  {
    value: 'delivered',
    label: () => t('CAMPAIGN.WHATSAPP.REPORT.STATUS_BADGE.DELIVERED'),
  },
  {
    value: 'read',
    label: () => t('CAMPAIGN.WHATSAPP.REPORT.STATUS_BADGE.READ'),
  },
  {
    value: 'failed',
    label: () => t('CAMPAIGN.WHATSAPP.REPORT.STATUS_BADGE.FAILED'),
  },
  {
    value: 'pending',
    label: () => t('CAMPAIGN.WHATSAPP.REPORT.STATUS_BADGE.PENDING'),
  },
];

const hasDeliveries = computed(() => report.value?.total > 0);
const hasFilteredResults = computed(() => report.value?.deliveries?.length > 0);
const totalPages = computed(() => report.value?.total_pages || 1);
const totalFiltered = computed(() => report.value?.total_filtered || 0);

const summaryCards = computed(() => [
  {
    label: t('CAMPAIGN.WHATSAPP.REPORT.TOTAL'),
    value: report.value?.total || 0,
    color: 'bg-slate-100 dark:bg-n-solid-2 text-slate-800 dark:text-n-slate-12',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORT.SENT'),
    value: report.value?.sent || 0,
    color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORT.DELIVERED'),
    value: report.value?.delivered || 0,
    color:
      'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORT.READ'),
    value: report.value?.read || 0,
    color:
      'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORT.FAILED'),
    value: report.value?.failed || 0,
    color: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORT.PENDING'),
    value: report.value?.pending || 0,
    color:
      'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
  },
]);

const statusBadgeClass = status => {
  const classes = {
    sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    delivered:
      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    read: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    pending:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  };
  return classes[status] || 'bg-slate-100 text-slate-800';
};

const statusLabel = status => {
  if (!status) return '—';
  const key = status.toUpperCase();
  const translations = {
    SENT: t('CAMPAIGN.WHATSAPP.REPORT.STATUS_BADGE.SENT'),
    DELIVERED: t('CAMPAIGN.WHATSAPP.REPORT.STATUS_BADGE.DELIVERED'),
    READ: t('CAMPAIGN.WHATSAPP.REPORT.STATUS_BADGE.READ'),
    FAILED: t('CAMPAIGN.WHATSAPP.REPORT.STATUS_BADGE.FAILED'),
    PENDING: t('CAMPAIGN.WHATSAPP.REPORT.STATUS_BADGE.PENDING'),
  };
  return translations[key] || status;
};

const formatDate = dateStr => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
};

const goBack = () => {
  router.push({ name: 'campaigns_whatsapp_index' });
};

let searchTimeout = null;
const onSearchInput = value => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    searchTerm.value = value;
    currentPage.value = 1;
  }, 350);
};

const onStatusChange = value => {
  statusFilter.value = value;
  currentPage.value = 1;
};

const goToPage = page => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const fetchReport = async () => {
  if (!campaignId.value) return;

  try {
    const params = {
      page: currentPage.value,
      per_page: perPage.value,
    };
    if (statusFilter.value) params.status = statusFilter.value;
    if (searchTerm.value) params.search = searchTerm.value;

    await store.dispatch('campaigns/getReport', {
      campaignId: campaignId.value,
      params,
    });
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP.REPORT.ERROR_LOADING'));
  }
};

// Re-fetch when campaign changes (navigation between campaigns)
watch(campaignId, () => {
  currentPage.value = 1;
  searchTerm.value = '';
  statusFilter.value = '';
  fetchReport();
});

watch([currentPage, perPage, statusFilter, searchTerm], fetchReport);

onMounted(fetchReport);
</script>

<template>
  <div class="flex flex-col h-full overflow-auto">
    <!-- Header -->
    <header
      class="flex items-center gap-3 px-6 py-4 bg-white dark:bg-n-black/30 border-b border-slate-100 dark:border-n-weak"
    >
      <button
        class="flex items-center gap-1 text-sm text-slate-600 dark:text-n-slate-11 hover:text-slate-900 dark:hover:text-n-slate-12"
        @click="goBack"
      >
        <Icon icon="i-lucide-arrow-left" class="size-4" />
        {{ t('CAMPAIGN.WHATSAPP.REPORT.BACK') }}
      </button>
      <div class="flex-1">
        <h1 class="text-lg font-semibold text-slate-900 dark:text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.REPORT.TITLE') }}
        </h1>
        <p
          v-if="report?.title"
          class="text-sm text-slate-500 dark:text-n-slate-10"
        >
          {{ report.title }}
        </p>
      </div>

      <button
        v-if="!isLoading"
        class="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500 focus:ring-offset-2 dark:focus:ring-offset-n-black"
        :disabled="isDownloading || !hasDeliveries"
        @click="downloadReport"
      >
        <Spinner v-if="isDownloading" :size="16" />
        <Icon v-else icon="i-lucide-download" class="size-4" />
        {{ t('CAMPAIGN.WHATSAPP.REPORT.DOWNLOAD') }}
      </button>
    </header>

    <!-- Loading (initial) -->
    <div
      v-if="isLoading && !report?.total"
      class="flex items-center justify-center flex-1"
    >
      <Spinner />
    </div>

    <!-- Content -->
    <div v-else class="flex-1 p-6 space-y-6">
      <!-- Summary Cards -->
      <section>
        <h2
          class="text-sm font-medium text-slate-500 dark:text-n-slate-10 mb-3"
        >
          {{ t('CAMPAIGN.WHATSAPP.REPORT.SUMMARY') }}
        </h2>
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <div
            v-for="card in summaryCards"
            :key="card.label"
            :class="card.color"
            class="rounded-xl p-4 text-center"
          >
            <p class="text-2xl font-bold">{{ card.value }}</p>
            <p class="text-xs mt-1 opacity-80">{{ card.label }}</p>
          </div>
        </div>
      </section>

      <!-- Table section -->
      <section v-if="hasDeliveries">
        <h2
          class="text-sm font-medium text-slate-500 dark:text-n-slate-10 mb-3"
        >
          {{ t('CAMPAIGN.WHATSAPP.REPORT.DELIVERIES_TABLE.TITLE') }}
        </h2>

        <!-- Search + Filters bar -->
        <div class="flex flex-wrap items-center gap-3 mb-4">
          <!-- Search -->
          <div class="flex-1 min-w-[200px] max-w-sm">
            <input
              type="text"
              :placeholder="t('CAMPAIGN.WHATSAPP.REPORT.SEARCH_PLACEHOLDER')"
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500/30"
              @input="onSearchInput($event.target.value)"
            />
          </div>

          <!-- Status filter -->
          <select
            :value="statusFilter"
            class="px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-n-weak bg-white dark:bg-n-solid-1 text-slate-900 dark:text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30"
            @change="onStatusChange($event.target.value)"
          >
            <option
              v-for="opt in statusOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label() }}
            </option>
          </select>

          <!-- Loading indicator for filter/page changes -->
          <Spinner v-if="isLoading" class="size-5" />
        </div>

        <!-- Table -->
        <div
          v-if="hasFilteredResults"
          class="overflow-x-auto rounded-xl border border-slate-200 dark:border-n-weak"
        >
          <table
            class="min-w-full divide-y divide-slate-200 dark:divide-n-weak"
          >
            <thead class="bg-slate-50 dark:bg-n-solid-2">
              <tr>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-n-slate-10 uppercase tracking-wider"
                >
                  {{
                    t('CAMPAIGN.WHATSAPP.REPORT.DELIVERIES_TABLE.CONTACT_NAME')
                  }}
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-n-slate-10 uppercase tracking-wider"
                >
                  {{ t('CAMPAIGN.WHATSAPP.REPORT.DELIVERIES_TABLE.PHONE') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-n-slate-10 uppercase tracking-wider"
                >
                  {{ t('CAMPAIGN.WHATSAPP.REPORT.DELIVERIES_TABLE.STATUS') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-n-slate-10 uppercase tracking-wider"
                >
                  {{ t('CAMPAIGN.WHATSAPP.REPORT.DELIVERIES_TABLE.SENT_AT') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-n-slate-10 uppercase tracking-wider"
                >
                  {{
                    t('CAMPAIGN.WHATSAPP.REPORT.DELIVERIES_TABLE.DELIVERED_AT')
                  }}
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-n-slate-10 uppercase tracking-wider"
                >
                  {{ t('CAMPAIGN.WHATSAPP.REPORT.DELIVERIES_TABLE.READ_AT') }}
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-n-slate-10 uppercase tracking-wider"
                >
                  {{ t('CAMPAIGN.WHATSAPP.REPORT.DELIVERIES_TABLE.ERROR') }}
                </th>
              </tr>
            </thead>
            <tbody
              class="bg-white dark:bg-n-solid-1 divide-y divide-slate-200 dark:divide-n-weak"
            >
              <tr
                v-for="delivery in report.deliveries"
                :key="delivery.contact_id"
              >
                <td
                  class="px-4 py-3 text-sm text-slate-900 dark:text-n-slate-12 whitespace-nowrap"
                >
                  {{ delivery.contact_name || '—' }}
                </td>
                <td
                  class="px-4 py-3 text-sm text-slate-500 dark:text-n-slate-10 whitespace-nowrap"
                >
                  {{ delivery.contact_phone || '—' }}
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <span
                    :class="statusBadgeClass(delivery.delivery_status)"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ statusLabel(delivery.delivery_status) }}
                  </span>
                </td>
                <td
                  class="px-4 py-3 text-sm text-slate-500 dark:text-n-slate-10 whitespace-nowrap"
                >
                  {{ formatDate(delivery.sent_at) }}
                </td>
                <td
                  class="px-4 py-3 text-sm text-slate-500 dark:text-n-slate-10 whitespace-nowrap"
                >
                  {{ formatDate(delivery.delivered_at) }}
                </td>
                <td
                  class="px-4 py-3 text-sm text-slate-500 dark:text-n-slate-10 whitespace-nowrap"
                >
                  {{ formatDate(delivery.read_at) }}
                </td>
                <td
                  class="px-4 py-3 text-sm text-red-500 dark:text-red-400 whitespace-nowrap max-w-xs truncate"
                >
                  {{ delivery.error_message || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- No filtered results -->
        <div
          v-else
          class="py-8 text-center text-sm text-slate-500 dark:text-n-slate-10"
        >
          {{ t('CAMPAIGN.WHATSAPP.REPORT.NO_RESULTS') }}
        </div>

        <!-- Pagination footer -->
        <div v-if="totalPages > 1" class="mt-4 pb-4">
          <PaginationFooter
            :current-page="currentPage"
            :total-items="totalFiltered"
            :items-per-page="perPage"
            @update:current-page="goToPage"
          />
        </div>
      </section>

      <!-- Empty State (no deliveries at all) -->
      <div
        v-else-if="!isLoading"
        class="flex flex-col items-center justify-center py-16 text-center"
      >
        <Icon
          icon="i-lucide-bar-chart-2"
          class="size-12 text-slate-300 dark:text-n-slate-7 mb-4"
        />
        <h3 class="text-base font-medium text-slate-700 dark:text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.REPORT.EMPTY_STATE.TITLE') }}
        </h3>
        <p class="text-sm text-slate-500 dark:text-n-slate-10 mt-1">
          {{ t('CAMPAIGN.WHATSAPP.REPORT.EMPTY_STATE.SUBTITLE') }}
        </p>
      </div>
    </div>
  </div>
</template>
